import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";

type Addon = {
  id: string;
  name: string;
  price: number;
  description: string | null;
};

type EditAddonDialogProps = {
  addon: Addon | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
};

export function EditAddonDialog({ addon, open, onOpenChange, onSuccess }: EditAddonDialogProps) {
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const [description, setDescription] = useState("");

  useEffect(() => {
    if (addon) {
      setName(addon.name);
      setPrice(addon.price.toString());
      setDescription(addon.description || "");
    } else {
      setName("");
      setPrice("");
      setDescription("");
    }
  }, [addon]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setLoading(true);
    try {
      if (addon) {
        // Update existing addon
        const { error } = await supabase
          .from("pricing_addons")
          .update({
            name,
            price: parseFloat(price),
            description: description || null,
          })
          .eq("id", addon.id);

        if (error) throw error;
      } else {
        // Create new addon
        const { error } = await supabase.from("pricing_addons").insert({
          name,
          price: parseFloat(price),
          description: description || null,
        });

        if (error) throw error;
      }

      toast({
        title: "Success",
        description: addon ? "Add-on updated successfully" : "Add-on created successfully",
      });

      onSuccess();
      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{addon ? "Edit Add-on" : "Add New Add-on"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="price">Price (£/year)</Label>
            <Input
              id="price"
              type="number"
              step="0.01"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {addon ? "Save Changes" : "Create Add-on"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
